import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// styles
import Styles from './spotWalletTab.module.css';

// icons
import { RiShareBoxFill } from 'react-icons/ri';
import { FiEye, FiEyeOff, FiSearch, FiMoreVertical } from 'react-icons/fi';
import { ReactComponent as WalletIcon } from '../../../../assets/icons/imported/wallet.svg';

// components
import SpotWalletFooter from './SpotWalletFooter';
import Button from '../../../../components/Button/Button';
import CoinTag from '../../../../components/CoinTag/CoinTag';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { _getFundsData, _getDepositAddress } from '../../../../redux/actions';
import {
  setSelectCurrencyModalData,
  setSelectCurrencyModalOpen
} from '../../../modals/select-currency/redux/selectCurrencyModalSlice';
import { _getBalances, _updateBalancePreference } from '../../redux/actions';
import { setWalletDepositModalOpen } from '../../../modals/deposit-withdraw/redux/walletDepositModalSlice';
import { setBankDetails, setCryptoDetails, setDepositData } from '../../../../redux/slices/depositSlice';

// utils
import { injectIntl } from 'react-intl';
import { formatNumber } from '../../../helpers/formatNumber';
import { debounce } from '../../../../utility/debounce';
import { handleWithdraw } from '../../../../utility/deposit-withdraw-helpers/handleWithdraw';
import { setUser } from '../../../user/redux/slices/userDataSlice';
import { showErrorSnackbar } from '../../../snackbar/helpers';
import {
  setBtcBalance,
  setDeposits,
  setFunds,
  setSpotBalanceBTC,
  setSpotBalanceUSDT,
  setWithdrawals
} from '../../../../redux/slices/fundsSlice';

// Currency name mapping
const getCurrencyDisplayName = (currencyCode) => {
  const currencyMap = {
    // Cryptocurrencies
    'btc': 'Bitcoin',
    'eth': 'Ethereum',
    'sol': 'Solana',
    'trx': 'TRON',
    'flr': 'Flare',
    'xrp': 'Ripple',
    'xlm': 'Stellar',
    'aave': 'Aave',
    'aca': 'Acala',
    'bnc': 'Bifrost Native Coin',
    'sgb': 'Songbird',
    'xdg': 'Dogechain',
    '1inch': '1inch',
    'ada': 'Cardano',
    'dot': 'Polkadot',
    'usdt': 'Tether USD',
    'usdc': 'USD Coin',
    'bnb': 'BNB',
    'matic': 'Polygon',
    'link': 'Chainlink',
    'avax': 'Avalanche',
    'atom': 'Cosmos',
    'algo': 'Algorand',
    'xtz': 'Tezos',
    'near': 'NEAR Protocol',

    // Fiat currencies
    'usd': 'US Dollar',
    'eur': 'Euro',
    'gbp': 'British Pound',
    'jpy': 'Japanese Yen',
    'cad': 'Canadian Dollar',
    'aud': 'Australian Dollar',
    'chf': 'Swiss Franc',
    'cny': 'Chinese Yuan',
    'krw': 'South Korean Won',
    'inr': 'Indian Rupee'
  };

  return currencyMap[currencyCode?.toLowerCase()] || currencyCode?.toUpperCase() || 'Unknown';
};

// Helper function to check if a currency is one of our target fiat currencies
const isTargetFiatCurrency = (currencyCode) => {
  const targetFiatCurrencies = ['usd', 'eur', 'gbp'];
  return targetFiatCurrencies.includes(currencyCode?.toLowerCase());
};


function SpotWalletTab() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const exchangeName = useSelector(state => state.exchangeCustomization.value.name)

  document.title = `${exchangeName}: Spot Wallet`

  const theme = useSelector(state => state.theme.value)
  const currencies = useSelector(state => state.currencies.value)

  // ** Show/Hide Price
  const hideBalance = useSelector(state => state.userData.value.hide_balance)

  const TotalWithdraws = useSelector(state => state.fundsData.data.total_withdrawals)
  const TotalDeposits = useSelector(state => state.fundsData.data.total_deposits)
  const TotalFunds = useSelector(state => state.fundsData.data.total_funds)
  const totalBalance = useSelector(state => state.fundsData.data)
  const [selectedTab, setSelectedTab] = useState('available')
  const [filteredBalances, setFilteredBalances] = useState([])
  const [filteredFiatBalances, setFilteredFiatBalances] = useState([])
  const [filteredCryptoBalances, setFilteredCryptoBalances] = useState([])
  const [loading, setLoading] = useState(false)
  const [portfolioMetrics, setPortfolioMetrics] = useState(null)
  // wallet table states
  const pageLimit = 8
  const [tableLoading, setTableLoading] = useState(false)
  const [hideBalanceLoading, setHideBalanceLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(1)
  const searchInputRef = useRef(null)
  const fiatSearchInputRef = useRef(null)
  const cryptoSearchInputRef = useRef(null)

  // Add state for dropdown menu
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [depositLoading, setDepositLoading] = useState(false)

  const handlePageChange = (e, value) => {
    setPage(value)
  }

  // Function to handle dropdown toggle
  const toggleDropdown = (walletId) => {
    setActiveDropdown(activeDropdown === walletId ? null : walletId)
  }

  // Function to close dropdown when clicking outside
  const closeDropdown = () => {
    setActiveDropdown(null)
  }

  // Add click outside listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(`.${Styles.dropdownContainer}`)) {
        closeDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdown])

  // function to fetch fiat wallets (USD, EUR, GBP only)
  const fetchFiatWallets = (searchParams = {}) => {
    setTableLoading(true)

    // Define the fiat currencies we want to show in the desired order
    const targetFiatCurrencies = ['usd', 'gbp', 'eur']

    _getBalances(
      {
        // Remove nonzero parameter to always show USD, GBP, EUR even with 0 balance
        ...searchParams
      },
      ({ data, page, total }) => {
        // Create a map of existing balances for quick lookup
        const balanceMap = {}
        data?.forEach(balance => {
          if (targetFiatCurrencies.includes(balance.currency?.toLowerCase())) {
            balanceMap[balance.currency?.toLowerCase()] = balance
          }
        })

        // Ensure all target fiat currencies are included, even with 0 balance
        const balances = targetFiatCurrencies.map(currencyCode => {
          const existingBalance = balanceMap[currencyCode]
          const coinData = currencies?.find(currency => currency?.id?.toLowerCase() === currencyCode)
          const displayName = getCurrencyDisplayName(currencyCode)

          return {
            ...coinData,
            currency: currencyCode.toUpperCase(),
            balance: existingBalance?.balance || '0.00000000',
            locked: existingBalance?.locked || '0.00000000',
            displayName,
            name: displayName,
            icon_url: coinData?.icon_url || null,
            id: currencyCode.toUpperCase()
          }
        })

        setFilteredFiatBalances(balances)
        setTableLoading(false)
      },
      err => {
        // Even on error, show the fiat currencies with 0 balance
        const targetFiatCurrencies = ['usd', 'gbp', 'eur']
        const balances = targetFiatCurrencies.map(currencyCode => {
          const coinData = currencies?.find(currency => currency?.id?.toLowerCase() === currencyCode)
          const displayName = getCurrencyDisplayName(currencyCode)

          return {
            ...coinData,
            currency: currencyCode.toUpperCase(),
            balance: '0.00000000',
            locked: '0.00000000',
            displayName,
            name: displayName,
            icon_url: coinData?.icon_url || null,
            id: currencyCode.toUpperCase()
          }
        })
        setFilteredFiatBalances(balances)
        setTableLoading(false)
      }
    )
  }

  // function to fetch crypto wallets (exclude fiat currencies)
  const fetchCryptoWallets = (searchParams = {}) => {
    setTableLoading(true)

    // Define all fiat currencies to exclude
    const allFiatCurrencies = ['usd', 'eur', 'gbp', 'jpy', 'cad', 'aud', 'chf', 'cny', 'krw', 'inr']

    _getBalances(
      {
        limit: pageLimit,
        page,
        nonzero: selectedTab === 'available',
        ...searchParams
      },
      ({ data, page, total }) => {
        // Filter out all fiat currencies from the response
        const cryptoData = data?.filter(balance =>
          !allFiatCurrencies.includes(balance.currency?.toLowerCase())
        ) || []

        const balances = cryptoData.map(balance => {
          const coinData = currencies?.find(currency => currency?.id === balance?.currency)
          const displayName = getCurrencyDisplayName(balance?.currency)
          return {
            ...coinData,
            ...balance,
            displayName,
            name: displayName,
            icon_url: coinData?.icon_url || null
          }
        })
        setFilteredCryptoBalances(balances)
        setTotalRows(total) // Keep pagination for crypto section
        setTableLoading(false)
      },
      err => {
        setFilteredCryptoBalances([])
        setTableLoading(false)
      }
    )
  }

  // function to fetch all wallets (for pagination total count)
  const fetchAllWallets = () => {
    _getBalances(
      {
        limit: pageLimit,
        page,
        nonzero: selectedTab === 'available'
      },
      ({ data, page, total }) => {
        const balances = data?.map(balance => {
          const coinData = currencies?.find(currency => currency?.id === balance?.currency)
          const displayName = getCurrencyDisplayName(balance?.currency)
          return {
            ...coinData,
            ...balance,
            displayName,
            name: displayName,
            icon_url: coinData?.icon_url || null
          }
        })
        setFilteredBalances(balances || [])
        setTotalRows(total)
      },
      err => {
        setFilteredBalances([])
        setTotalRows(0)
      }
    )
  }

  // Main function to fetch both wallet types
  const fetchWallets = () => {
    setTableLoading(true)
    setFilteredBalances([])
    setFilteredFiatBalances([])
    setFilteredCryptoBalances([])

    // Fetch fiat wallets (no pagination, only USD, EUR, GBP)
    fetchFiatWallets()

    // Fetch crypto wallets (with pagination, excluding fiat currencies)
    fetchCryptoWallets()

    // Fetch all wallets for total count
    fetchAllWallets()
  }

  // Search function for fiat wallets
  const fetchFiatWalletsSearch = () => {
    const searchValue = fiatSearchInputRef.current?.value || ''
    if (searchValue === '') {
      fetchFiatWallets()
      return
    }

    // Fetch all data and then filter on frontend
    _getBalances(
      {
        nonzero: selectedTab === 'available'
      },
      ({ data, page, total }) => {
        // Filter to only include USD, EUR, GBP and match search term
        const targetFiatCurrencies = ['usd', 'eur', 'gbp']
        const fiatData = data?.filter(balance => {
          const currency = balance.currency?.toLowerCase()
          const displayName = getCurrencyDisplayName(balance.currency).toLowerCase()
          const searchTerm = searchValue.toLowerCase()

          return targetFiatCurrencies.includes(currency) &&
            (currency.includes(searchTerm) || displayName.includes(searchTerm))
        }) || []

        const balances = fiatData.map(balance => {
          const coinData = currencies?.find(currency => currency?.id === balance?.currency)
          const displayName = getCurrencyDisplayName(balance?.currency)
          return {
            ...coinData,
            ...balance,
            displayName,
            name: displayName,
            icon_url: coinData?.icon_url || null
          }
        })
        setFilteredFiatBalances(balances)
        setTableLoading(false)
      },
      err => {
        setFilteredFiatBalances([])
        setTableLoading(false)
      }
    )
  }

  // Search function for crypto wallets
  const fetchCryptoWalletsSearch = () => {
    const searchValue = cryptoSearchInputRef.current?.value || ''
    if (searchValue === '') {
      fetchCryptoWallets()
      return
    }

    const searchParams = {
      'search[currency_code]': searchValue,
      'search[currency_name]': searchValue
    }
    fetchCryptoWallets(searchParams)
  }

  // Create debounced search functions for each section
  const debouncedFiatSearch = debounce(() => {
    fetchFiatWalletsSearch()
  }, 500)

  const debouncedCryptoSearch = debounce(() => {
    fetchCryptoWalletsSearch()
  }, 500)

  useEffect(() => {
    // Check search inputs for values
    const fiatSearchValue = fiatSearchInputRef.current?.value || ''
    const cryptoSearchValue = cryptoSearchInputRef.current?.value || ''

    // Fetch fiat data (not affected by pagination)
    if (fiatSearchValue) {
      fetchFiatWalletsSearch()
    } else {
      fetchFiatWallets()
    }

    // Fetch crypto data (affected by pagination)
    if (cryptoSearchValue) {
      fetchCryptoWalletsSearch()
    } else {
      fetchCryptoWallets()
    }

    // Fetch all wallets for total count
    fetchAllWallets()
  }, [selectedTab, page])

  useEffect(() => {
    // Always default to 'available' tab unless explicitly navigating to 'all'
    if (location.pathname === '/account/wallet/spot/all') {
      setSelectedTab('all')
    } else {
      // For any other path (/account/wallet/spot/available or /account/wallet/spot), default to available
      setSelectedTab('available')
      if (location.pathname === '/account/wallet/spot') {
        navigate('/account/wallet/spot/available', { replace: true })
      }
    }
  }, []) // Remove location dependency to prevent re-running on URL changes

  const handleDeposit = (coin_id) => {
    setDepositLoading(true)
    dispatch(setWalletDepositModalOpen(true))
    // _getDepositAddress(
    //   {
    //     currency: coin_id
    //   },
    //   (data) => {
    //     setDepositLoading(false)
    //     // Check response format and dispatch appropriate action
    //     if (data.iban || data.account_holder_full_name) {
    //       // Bank details format (fiat currencies)
    //       dispatch(setBankDetails(data))
    //       dispatch(setWalletDepositModalOpen(true))
    //     } else if (data.address && data.currency) {
    //       // Crypto address format (crypto currencies)
    //       dispatch(setCryptoDetails(data))
    //       dispatch(setWalletDepositModalOpen(true))
    //     } else {
    //       showErrorSnackbar({ alertMessage: 'Deposit information not available' })
    //     }
    //   },
    //   (err) => {
    //     setDepositLoading(false)
    //     showErrorSnackbar({ alertMessage: 'Failed to get deposit information' })
    //   }
    // )
  }

  // ----- RESPONSIVENESS STATE ---------
  // ------------------------------------
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 820)

  const handleResize = () => {
    setIsMobileView(window.innerWidth < 820)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    _getFundsData(
      data => {
        dispatch(setWithdrawals(data.total_withdraw))
        dispatch(setDeposits(data.total_deposit))
        dispatch(setFunds(data.total_balance))
        dispatch(setBtcBalance(data.total_balance_btc))
        dispatch(setSpotBalanceUSDT(data.total_spot_balance))
        dispatch(setSpotBalanceBTC(data.total_spot_balance_btc))

        // Extract portfolio metrics from the updated API response
        const metrics = {
          profitLoss: parseFloat(data.pl_amount || 0),
          profitLossPercentage: parseFloat(data.pl_percentage || 0)
        }
        setPortfolioMetrics(metrics)

        setLoading(false)
      },
      error => {
        console.log(error)
        setLoading(false)
      }
    )
  }, [])
  // ------------------------------------
  // ------------------------------------


  // ** Handlers
  const handleToggleHidePrice = () => {
    setHideBalanceLoading(true)
    _updateBalancePreference(
      { preference: !hideBalance },
      data => {
        dispatch(setUser(data.data))
        setHideBalanceLoading(false)
      },
      () => {
        setHideBalanceLoading(false)
        showErrorSnackbar({ alertMessage: 'Something went wrong' })
      }
    )
  }

  // Reusable wallet cards component
  const renderWalletCards = (wallets, isLoading = false) => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <div key={`loading-${index}`} className={Styles.walletCard}>
          <div className={Styles.walletGrid}>
            {/* Coin Info Shimmer */}
            <div className={Styles.coinInfo}>
              <div className={Styles.shimmerEffect} style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%'
              }}></div>
              <div className={Styles.coinDetails}>
                <div className={Styles.shimmerEffect} style={{
                  height: '16px',
                  borderRadius: '4px',
                  width: '80px',
                  marginBottom: '4px'
                }}></div>
                <div className={Styles.shimmerEffect} style={{
                  height: '12px',
                  borderRadius: '4px',
                  width: '40px'
                }}></div>
              </div>
            </div>

            {/* Other columns shimmer */}
            {Array.from({ length: 3 }).map((_, colIndex) => (
              <div key={colIndex} className={Styles.walletInfo}>
                <div className={Styles.shimmerEffect} style={{
                  height: '12px',
                  borderRadius: '4px',
                  marginBottom: '4px'
                }}></div>
                <div className={Styles.shimmerEffect} style={{
                  height: '16px',
                  borderRadius: '4px',
                  width: '60%'
                }}></div>
              </div>
            ))}

            {/* Action Button Shimmer */}
            <div className={Styles.walletActions}>
              <div className={Styles.shimmerEffect} style={{
                height: '32px',
                width: '32px',
                borderRadius: '6px'
              }}></div>
            </div>
          </div>
        </div>
      ))
    }

    return wallets.map((wallet, index) => (
      <div key={wallet.currency} className={`${Styles.walletCard} ${activeDropdown === wallet.currency ? Styles.activeDropdown : ''}`}>
        <div className={Styles.walletGrid}>
          {/* Coin Info */}
          <div className={Styles.coinInfo}>
            <div className={Styles.coinIconContainer}>
              {wallet?.icon_url ? (
                <img src={wallet.icon_url} alt={wallet?.displayName || wallet?.name} className={Styles.coinIcon} />
              ) : (
                <div className={Styles.coinIconFallback}>
                  {(wallet?.currency?.toUpperCase() || '?').slice(0, 3)}
                </div>
              )}
            </div>
            <div className={Styles.coinDetails}>
              <div className={Styles.coinName}>
                {wallet?.displayName || wallet?.name || getCurrencyDisplayName(wallet?.currency)}
              </div>
              <div className={Styles.coinTag}>
                <CoinTag tag={wallet?.currency?.toUpperCase()} size={10} />
              </div>
            </div>
          </div>

          {/* Available Balance */}
          <div className={Styles.walletInfo}>
            <div className={Styles.walletLabel}>Available Balance</div>
            <div className={Styles.walletValue}>
              {hideBalance ? '••••••••' : formatNumber(wallet.balance, 8)}
            </div>
          </div>

          {/* Locked */}
          <div className={Styles.walletInfo}>
            <div className={Styles.walletLabel}>Locked</div>
            <div className={Styles.walletLocked}>
              {hideBalance ? '••••' : formatNumber(wallet.locked, 2)}
            </div>
          </div>

          {/* Total Amount */}
          <div className={Styles.walletInfo}>
            <div className={Styles.walletLabel}>Total Amount</div>
            <div className={Styles.walletAmount}>
              {hideBalance ? '••••••••' : formatNumber(parseFloat(wallet.balance) + parseFloat(wallet.locked), 2)}
            </div>
          </div>

          {/* Action Button */}
          <div className={Styles.walletActions}>
            <div className={Styles.dropdownContainer}>
              <button
                className={Styles.actionButton}
                onClick={() => toggleDropdown(wallet.currency)}
              >
                <FiMoreVertical size={16} />
              </button>

              {activeDropdown === wallet.currency && (
                <div className={Styles.dropdownMenu}>
                  <button
                    className={Styles.dropdownItem}
                    onClick={() => {
                      const currentCoinId = wallet.id
                      // Set the coin data first, then call handleDeposit
                      const coinData = currencies?.find(currency => currency?.id === currentCoinId)
                      if (coinData) {
                        dispatch(setDepositData(coinData))
                      }
                      handleDeposit(currentCoinId)
                      closeDropdown()
                    }}
                    disabled={depositLoading}
                  >
                    <RiShareBoxFill size={14} />
                    <span>{depositLoading ? 'Loading...' : 'Deposit'}</span>
                  </button>
                  <button
                    className={Styles.dropdownItem}
                    onClick={() => {
                      handleWithdraw({ selectedCoinId: wallet.id })
                      closeDropdown()
                    }}
                  >
                    <RiShareBoxFill size={14} />
                    <span>Withdraw</span>
                  </button>
                  <button
                    className={Styles.dropdownItem}
                    onClick={() => {
                      navigate(`/convert/${wallet.currency.toUpperCase()}_USD`)
                      closeDropdown()
                    }}
                  >
                    <RiShareBoxFill size={14} />
                    <span>Convert</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    ))
  }

  return (
    <div className={Styles.modernWalletContainer}>
      {/* Modern Wallet Header */}
      <div className={Styles.walletHeader} style={{
        backgroundColor: 'var(--secondary-color)',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '0px',
        border: '1px solid rgba(139, 154, 171, 0.1)'
      }}>
        <div className={Styles.headerTop}>
          <div className={Styles.titleSection}>
            <h1 className={Styles.walletTitle}>Spot wallet</h1>
            <p className={Styles.walletSubtitle}>
              All your crypto techs, stored in one place. Cool huh?
            </p>
          </div>

          <div className={Styles.actionButtons}>
            <Button
              text={depositLoading ? "Loading..." : "Deposit"}
              disabled={depositLoading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                opacity: depositLoading ? 0.6 : 1
              }}
              onClick={() => {
                dispatch(
                  setSelectCurrencyModalData({
                    title: 'Select coin to deposit',
                    showTabs: false,
                    type: 'coin',
                    searchPlaceholder: 'Search coin name',
                    currenciesToHide: [],
                    disableBackdropClick: true,
                    callback: coin => {
                      // Set the coin data first, then call handleDeposit
                      dispatch(setDepositData(coin))
                      handleDeposit(coin.id)
                    }
                  })
                )
                dispatch(setSelectCurrencyModalOpen(true))
              }}
            />
            <Button
              text="Withdraw"
              variant="dark"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "transparent",
                border: `1px solid ${theme === "light" ? "rgba(139, 154, 171, 0.3)" : "rgba(139, 154, 171, 0.3)"}`,
                color: theme === "light" ? "#001e28" : "#ffffff"
              }}
              onClick={handleWithdraw}
            />
          </div>
        </div>

        <div className={Styles.overviewGrid}>
          {/* Left Column - Balance Display */}
          <div className={Styles.balanceColumn}>
            <div className={Styles.balanceDisplay}>
              <div className={Styles.balanceLabel}>
                <WalletIcon width={20} />
                <span>Wallet Balance</span>
              </div>

              <div className={Styles.balanceAmount}>
                <h2 className={Styles.balanceValue}>
                  {loading || !totalBalance || totalBalance.total_spot_balance === null || totalBalance.total_spot_balance === undefined ? (
                    <div className={Styles.shimmerEffect} style={{ width: '120px', height: '36px', display: 'inline-block' }}></div>
                  ) : (
                    <>
                      {hideBalance ? '••••••••' : `$${formatNumber(totalBalance?.total_spot_balance, 2)}`}
                      <span className={Styles.currency}>USD</span>
                    </>
                  )}
                </h2>

                <button
                  onClick={handleToggleHidePrice}
                  className={Styles.eyeButton}
                  disabled={hideBalanceLoading}
                >
                  {hideBalance ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                </button>
              </div>

              <p className={Styles.btcEquivalent}>
                ≈ {loading || !totalBalance || totalBalance.total_spot_balance_btc === null || totalBalance.total_spot_balance_btc === undefined ? (
                  <div className={Styles.shimmerEffect} style={{ width: '80px', height: '14px', display: 'inline-block' }}></div>
                ) : (
                  <>
                    {hideBalance ? '••••••••' : formatNumber(totalBalance?.total_spot_balance_btc, 8)} BTC
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Right Column - Stats Grid */}
          <div className={Styles.statsColumn}>
            <div className={Styles.statsGrid}>
              <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                <div className={Styles.statLabel} style={{ color: '#8b9aab' }}>
                  Total Deposited
                </div>
                <div className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                  {loading || !totalBalance || totalBalance.total_deposits === null || totalBalance.total_deposits === undefined ? (
                    <div className={Styles.shimmerEffect} style={{ width: '90px', height: '18px' }}></div>
                  ) : hideBalance ? (
                    '••••••••'
                  ) : (
                    `$${formatNumber(totalBalance?.total_deposits)}`
                  )}
                </div>
              </div>

              <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                <div className={Styles.statLabel} style={{ color: '#8b9aab' }}>
                  Total Withdrawn
                </div>
                <div className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                  {loading || !totalBalance || totalBalance.total_withdrawals === null || totalBalance.total_withdrawals === undefined ? (
                    <div className={Styles.shimmerEffect} style={{ width: '90px', height: '18px' }}></div>
                  ) : hideBalance ? (
                    '••••••••'
                  ) : (
                    `$${formatNumber(totalBalance?.total_withdrawals)}`
                  )}
                </div>
              </div>

              <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                <div className={Styles.statLabel} style={{ color: '#8b9aab' }}>
                  Active Wallets
                </div>
                <div className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                  {(filteredFiatBalances?.length || 0) + (filteredCryptoBalances?.length || 0)}
                </div>
              </div>

              <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                <div className={Styles.statLabel} style={{ color: '#8b9aab' }}>
                  Portfolio Growth
                </div>
                <div className={Styles.statValue} style={{
                  color: portfolioMetrics?.profitLoss >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {loading || !portfolioMetrics ? (
                    <div className={Styles.shimmerEffect} style={{ width: '60px', height: '18px' }}></div>
                  ) : hideBalance ? (
                    '••••••••'
                  ) : (
                    <>
                      {portfolioMetrics.profitLoss >= 0 ? '+' : ''}${formatNumber(Math.abs(portfolioMetrics.profitLoss), 2)}
                      {' '}({portfolioMetrics.profitLoss >= 0 ? '+' : ''}{portfolioMetrics.profitLossPercentage.toFixed(2)}%)
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fiat Wallets Section */}
      {(filteredFiatBalances.length > 0 || tableLoading) && (
        <div className={Styles.assetsSection} style={{
          backgroundColor: 'var(--secondary-color)',
          border: '1px solid rgba(139, 154, 171, 0.1)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px'
        }}>
          <div className={Styles.assetsHeader}>
            <h2 className={Styles.assetsTitle}>Fiat wallets</h2>

            {/* <div className={Styles.searchContainer}>
              <FiSearch className={Styles.searchIcon} />
              <input
                type="text"
                placeholder="Search fiat wallet"
                ref={fiatSearchInputRef}
                onChange={debouncedFiatSearch}
                className={Styles.searchInput}
              />
            </div> */}
          </div>

          {/* Filter Tabs */}
          {/* <div className={Styles.filterTabs}>
            <button
              className={`${Styles.filterTab} ${selectedTab === "all" ? Styles.activeTab : ""}`}
              onClick={() => {
                if (tableLoading) return
                navigate('/account/wallet/spot/all', { replace: true })
              }}
            >
              All
            </button>
            <button
              className={`${Styles.filterTab} ${selectedTab === "available" ? Styles.activeTab : ""}`}
              onClick={() => {
                if (tableLoading) return
                navigate('/account/wallet/spot/available', { replace: true })
              }}
            >
              Available
            </button>
          </div> */}

          {/* Fiat Wallets List */}
          <div className={Styles.walletsList}>
            {renderWalletCards(filteredFiatBalances, tableLoading && filteredFiatBalances.length === 0)}
          </div>

          {filteredFiatBalances.length === 0 && !tableLoading && (
            <div className={Styles.emptyState}>
              <p className={Styles.emptyTitle}>No fiat wallets found</p>
              <p className={Styles.emptyDescription}>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      )}

      {/* Crypto Wallets Section */}
      {(filteredCryptoBalances.length > 0 || tableLoading) && (
        <div className={Styles.assetsSection} style={{
          backgroundColor: 'var(--secondary-color)',
          border: '1px solid rgba(139, 154, 171, 0.1)',
          borderRadius: '16px',
          padding: '32px'
        }}>
          <div className={Styles.assetsHeader}>
            <h2 className={Styles.assetsTitle}>Crypto wallets</h2>

            <div className={Styles.searchContainer}>
              <FiSearch className={Styles.searchIcon} />
              <input
                type="text"
                placeholder="Search crypto wallet"
                ref={cryptoSearchInputRef}
                onChange={debouncedCryptoSearch}
                className={Styles.searchInput}
              />
            </div>
          </div>

          {/* Filter Tabs */}
          <div className={Styles.filterTabs}>
            <button
              className={`${Styles.filterTab} ${selectedTab === "all" ? Styles.activeTab : ""}`}
              onClick={() => {
                if (tableLoading) return
                if (selectedTab !== 'all') {
                  setTableLoading(true)
                  setSelectedTab('all')
                }
              }}
            >
              All
            </button>
            <button
              className={`${Styles.filterTab} ${selectedTab === "available" ? Styles.activeTab : ""}`}
              onClick={() => {
                if (tableLoading) return
                if (selectedTab !== 'available') {
                  setTableLoading(true)
                  setSelectedTab('available')
                }
              }}
            >
              Available
            </button>
          </div>

          {/* Crypto Wallets List */}
          <div className={Styles.walletsList}>
            {tableLoading ? renderWalletCards([], true) : renderWalletCards(filteredCryptoBalances, false)}
          </div>

          {filteredCryptoBalances.length === 0 && !tableLoading && (
            <div className={Styles.emptyState}>
              <p className={Styles.emptyTitle}>No crypto wallets found</p>
              <p className={Styles.emptyDescription}>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      )}

      {/* Show general empty state if no wallets at all */}
      {(filteredFiatBalances?.length || 0) === 0 && (filteredCryptoBalances?.length || 0) === 0 && !tableLoading && (
        <div className={Styles.assetsSection} style={{
          backgroundColor: 'var(--secondary-color)',
          border: '1px solid rgba(139, 154, 171, 0.1)',
          borderRadius: '16px',
          padding: '32px'
        }}>
          <div className={Styles.emptyState}>
            <p className={Styles.emptyTitle}>No wallets found</p>
            <p className={Styles.emptyDescription}>Try adjusting your search or filter criteria</p>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalRows > 0 && (
        <div className={Styles.paginationContainer} style={{ marginTop: '24px' }}>
          <span className={Styles.paginationInfo}>
            {((page - 1) * pageLimit) + 1}-{Math.min(page * pageLimit, totalRows)} of {totalRows}
          </span>

          <div className={Styles.paginationControls}>
            <button
              onClick={() => handlePageChange(null, page - 1)}
              disabled={page === 1}
              className={`${Styles.paginationButton} ${page === 1 ? Styles.disabled : ''}`}
            >
              ←
            </button>

            {Array.from({ length: Math.ceil(totalRows / pageLimit) }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(null, pageNum)}
                className={`${Styles.paginationButton} ${page === pageNum ? Styles.active : ''}`}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(null, page + 1)}
              disabled={page === Math.ceil(totalRows / pageLimit)}
              className={`${Styles.paginationButton} ${page === Math.ceil(totalRows / pageLimit) ? Styles.disabled : ''}`}
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* SpotWalletFooter */}
      {isMobileView && <SpotWalletFooter />}
    </div>
  )
}

export default injectIntl(SpotWalletTab)
